import dayjs from 'dayjs'
import type {
	SpeakingDailyTopic,
	SpeakingSessionCriteria,
	SpeakingTopicEditPayload,
	SpeakingTopicCategory,
} from 'src/apis/courseAiAPI'

// UD-671: wording and small rules of the Speaking part of the Course CMS, kept apart from the screens so
// they can be tested one by one.

export const SPEAKING_CATEGORY_LABELS: Record<string, string> = {
	MANUAL: 'Basic UniVini',
	HOT_TOPICS: 'Hot topic',
	IELTS_TOPICS: 'IELTS',
}

/** The three sets of a date, in the order the app shows them. */
export const SPEAKING_CATEGORY_ORDER: SpeakingTopicCategory[] = [
	'MANUAL',
	'HOT_TOPICS',
	'IELTS_TOPICS',
]

export const categoryLabel = (category?: string | null) =>
	(category && SPEAKING_CATEGORY_LABELS[category]) || category || '-'

const END_REASON_LABELS: Record<string, string> = {
	learner: 'Learner ended the talk',
	time_up: 'Time was up',
	network_timeout: 'Network lost, auto-submitted',
	deadline_sweep: 'Deadline passed, auto-submitted',
	server_error: 'Server error',
}

export const endReasonLabel = (
	reason?: string | null,
	status?: string | null,
) => {
	if (reason) return END_REASON_LABELS[reason] || reason
	if (status === 'active' || status === 'ending') return 'Still going'
	return '-'
}

export const modeLabel = (mode?: string | null) =>
	mode === 'ielts' ? 'IELTS' : 'Standard'

/** Dollars with 4 decimals: one talk costs a few cents. */
export const formatUsd = (value?: number | string | null) => {
	const amount = Number(value)
	return `$${(Number.isFinite(amount) ? amount : 0).toFixed(4)}`
}

/** 125000 ms as 2:05. */
export const formatDurationMs = (ms?: number | null) => {
	const total = Math.max(0, Math.round(Number(ms || 0) / 1000))
	const minutes = Math.floor(total / 60)
	const seconds = total % 60
	return `${minutes}:${String(seconds).padStart(2, '0')}`
}

export const CRITERIA_LABELS: [
	keyof SpeakingSessionCriteria & string,
	string,
][] = [
	['fluency', 'Fluency'],
	['vocabulary', 'Vocabulary'],
	['grammar', 'Grammar'],
	['pronunciation', 'Pronunciation'],
]

/** "Fluency 70, Vocabulary 75, ..." for the criteria the scoring produced; '-' while there are none. */
export const criteriaText = (criteria?: SpeakingSessionCriteria | null) => {
	if (!criteria) return '-'
	const parts = CRITERIA_LABELS.filter(
		([key]) => criteria[key] !== null && criteria[key] !== undefined,
	).map(([key, label]) => `${label} ${criteria[key]}`)
	return parts.length ? parts.join(', ') : '-'
}

export const scoreText = (score?: number | null) =>
	score === null || score === undefined
		? '-'
		: String(Math.round(Number(score)))

export const TURN_KIND_LABELS: Record<string, string> = {
	ai_open: 'AI opens',
	speech: 'Learner answers',
	silence: 'Silence',
	prep_done: 'Preparation done',
	ai_close: 'AI closes',
	canned: 'Standard line',
}

export const turnKindLabel = (kind?: string | null) =>
	(kind && TURN_KIND_LABELS[kind]) || kind || '-'

/** A correction the tutor gave in a turn, as a line: the shortest of the shapes the model uses. */
export const correctionText = (correction: any): string => {
	if (!correction) return ''
	if (typeof correction === 'string') return correction
	const said =
		correction.said ??
		correction.error ??
		correction.wrong ??
		correction.original
	const better =
		correction.better ??
		correction.correction ??
		correction.fix ??
		correction.corrected
	if (said && better) return `${said} -> ${better}`
	return (
		said ||
		better ||
		correction.explanation ||
		correction.text ||
		JSON.stringify(correction)
	)
}

// ---------------------------------------------------------------------------
// The date of the daily topics
// ---------------------------------------------------------------------------

export const DATE_FORMAT = 'YYYY-MM-DD'

export const shiftDate = (date: string, days: number) =>
	dayjs(date).add(days, 'day').format(DATE_FORMAT)

// dayjs without its strict parser moves 2026-02-31 to March, so the date is valid when it prints back unchanged.
export const isDateText = (value: string) =>
	/^\d{4}-\d{2}-\d{2}$/.test(value) &&
	dayjs(value).format(DATE_FORMAT) === value

/** Why a topic's Regenerate is off, or null when it works. Same rules as the server. */
export const regenerateBlockedReason = (
	topic: SpeakingDailyTopic,
): string | null => {
	if (topic.category === 'MANUAL') {
		return 'Basic UniVini topics come from the lessons, they are not regenerated'
	}
	if (topic.started > 0) {
		return `${topic.started} learner${topic.started === 1 ? '' : 's'} already started this topic`
	}
	if (!topic.can_regenerate) return 'A past date cannot be regenerated'
	return null
}

export const editBlockedReason = (topic: SpeakingDailyTopic): string | null =>
	topic.can_edit
		? null
		: `${topic.started} learner${topic.started === 1 ? '' : 's'} already started this topic, its text is fixed`

// ---------------------------------------------------------------------------
// The IELTS edit form: lists are typed one item to a line
// ---------------------------------------------------------------------------

export const linesToList = (text: string): string[] =>
	text
		.split(/\r?\n/)
		.map((line) => line.trim())
		.filter(Boolean)

export const listToLines = (items?: string[] | null) => (items || []).join('\n')

export interface IeltsFormValues {
	title: string
	part1_theme: string
	part1_questions: string
	cue_card: string
	bullets: string
	explain: string
	rounding_off: string
}

export const ieltsFormFromTopic = (
	topic: SpeakingDailyTopic,
): IeltsFormValues => ({
	title: topic.title || '',
	part1_theme: topic.ielts?.part1_theme || '',
	part1_questions: listToLines(
		topic.questions.map((question) => question.text),
	),
	cue_card: topic.ielts?.part2?.cue_card || '',
	bullets: listToLines(topic.ielts?.part2?.bullets),
	explain: topic.ielts?.part2?.explain || '',
	rounding_off: listToLines(topic.ielts?.rounding_off),
})

/** The messages for an IELTS edit that the server would refuse; the same counts as a generated set. */
export const ieltsFormErrors = (
	values: IeltsFormValues,
): Partial<Record<keyof IeltsFormValues, string>> => {
	const errors: Partial<Record<keyof IeltsFormValues, string>> = {}
	if (!values.title.trim()) errors.title = 'Title is required'
	if (!values.part1_theme.trim())
		errors.part1_theme = 'Part 1 theme is required'
	const questions = linesToList(values.part1_questions).length
	if (questions < 4 || questions > 5)
		errors.part1_questions = `Part 1 has 4 or 5 questions, one per line (now ${questions})`
	if (!values.cue_card.trim()) errors.cue_card = 'The cue card is required'
	const bullets = linesToList(values.bullets).length
	if (bullets < 3 || bullets > 4)
		errors.bullets = `The cue card has 3 or 4 bullets, one per line (now ${bullets})`
	if (!values.explain.trim())
		errors.explain = 'The closing line of the card is required'
	const rounding = linesToList(values.rounding_off).length
	if (rounding !== 2)
		errors.rounding_off = `Rounding off has 2 questions, one per line (now ${rounding})`
	return errors
}

export const ieltsFormToPayload = (
	values: IeltsFormValues,
): SpeakingTopicEditPayload => ({
	title: values.title.trim(),
	questions: linesToList(values.part1_questions).map((text) => ({ text })),
	ielts: {
		part1_theme: values.part1_theme.trim(),
		part2: {
			cue_card: values.cue_card.trim(),
			bullets: linesToList(values.bullets),
			explain: values.explain.trim(),
		},
		rounding_off: linesToList(values.rounding_off),
	},
})
